/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import SkeletonLoader from '@ion/dls/src/components/loading/SkeletonLoader';

export type LoaderProps = {
  classes: {
    loader: string,
    skeletonLoader: string,
  }
}

const useStyles = makeStyles(() => ({
  loader : {
    marginBottom : 24
  },
  skeletonLoader : {
    display    : 'flex',
    alignItems : 'center',
    padding    : '8px 16px',
    gap        : 8
  }
}));

/**
 * Defines the loader for an individual facet.
 */
const FacetLoader = ({classes}: LoaderProps) => (
  <div className={classes.skeletonLoader}>
    <SkeletonLoader width={20} height={20} />
    <SkeletonLoader width={180} height={16} />
  </div>
);

/**
 * Defines the loader for an facet section.
 */
const FacetLoaderSection = ({classes}: LoaderProps) => (
  <div className={classes.loader}>
    <SkeletonLoader width='100%' height={48} />
    <FacetLoader classes={classes} />
    <FacetLoader classes={classes} />
    <FacetLoader classes={classes} />
  </div>
);

/**
 * Defines the Job Facets Loader component to be used when loading the job facets.
 * 
 * ```typescript
 * import JobFacetsLoader from './JobFacetsLoader';
 * 
 * const MyComponent = () => {
 * 
 *   return (
 *     ...
 *     <ConditionalRender condition={isLoading}>
 *       <JobFacetsLoader />
 *     </ConditionalRender>
 *   );
 * }
 * ```
 * 
 * @returns - The Job Facets Loader component.
 */
const JobFacetsLoader = () => {
  const classes = useStyles();
  return (
    <>
      <FacetLoaderSection classes={classes} />
      <FacetLoaderSection classes={classes} />
      <FacetLoaderSection classes={classes} />
    </>
  );
};

export default JobFacetsLoader;
